// Optimized CRA-style SW registration (browser JS)
// - Strict PUBLIC_URL handling
// - No-store SW validation on localhost
// - updateViaCache: 'none' for faster rollouts
// - Visibility-aware periodic updates
// - Single reload on controllerchange
// - Clean error handling

const isLocalhost = Boolean(
    window.location.hostname === 'localhost' ||
    window.location.hostname === '[::1]' ||
    /^127(?:\.(?:25[0-5]|2[0-4]\d|[01]?\d?\d)){3}$/.test(window.location.hostname)
);

const isProd = process.env.NODE_ENV === 'production';
const supportsSW = 'serviceWorker' in navigator;

/**
 * Register the service worker in production.
 * @param {{ onUpdate?: (reg: ServiceWorkerRegistration) => void; onSuccess?: (reg: ServiceWorkerRegistration) => void }} [config]
 */
export function register(config) {
    if (!isProd || !supportsSW) return;

    // Resolve PUBLIC_URL against current location
    const PUBLIC_URL = process.env.PUBLIC_URL || '/';
    let publicUrl;
    try {
        publicUrl = new URL(PUBLIC_URL, window.location.href);
    } catch (err) {
        console.error(`Invalid PUBLIC_URL: "${PUBLIC_URL}". Using '/'.`, err);
        publicUrl = new URL('/', window.location.origin);
    }

    // Skip if PUBLIC_URL is on a different origin
    if (publicUrl.origin !== window.location.origin) {
        console.warn('SW skipped: PUBLIC_URL is on a different origin.');
        return;
    }

    // Build SW URL; strip trailing slash from pathname
    const basePath = publicUrl.pathname.replace(/\/+$/, '');
    const swUrl = `${basePath}/service-worker.js`;

    // Register after full load (keeps critical path clean)
    window.addEventListener('load', () => {
        if (isLocalhost) {
            checkValidServiceWorker(swUrl, config);
            navigator.serviceWorker.ready.then(() => {
                console.log('SW ready (localhost). See https://bit.ly/CRA-PWA');
            });
        } else {
            registerValidSW(swUrl, config);
        }
    });
}

/**
 * Register a valid service worker and attach update handling.
 * - updateViaCache: 'none' ensures fresh SW script fetch (no HTTP cache)
 * - Polls for updates while tab is visible
 */
function registerValidSW(swUrl, config) {
    navigator.serviceWorker
        .register(swUrl, { updateViaCache: 'none' })
        .then((registration) => {
            wireUpdateHandlers(registration, config);
            startVisibilityAwarePolling(registration);
            wireSingleReloadOnControllerChange();
        })
        .catch((error) => {
            console.error('SW registration failed:', error);
        });
}

/**
 * Check for an existing SW; unregister if not found or mismatched.
 * - Use { cache: 'no-store' } + 'Service-Worker: script' (MDN) to bypass caches
 */
function checkValidServiceWorker(swUrl, config) {
    fetch(swUrl, {
        cache: 'no-store',
        headers: { 'Service-Worker': 'script' }
    })
        .then((response) => {
            const contentType = response.headers.get('content-type') || '';
            if (response.status === 404 || !contentType.includes('javascript')) {
                // No SW found — unregister and hard reload to clear old caches
                navigator.serviceWorker.ready
                    .then((reg) => reg.unregister())
                    .then(() => window.location.reload());
            } else {
                // SW found — proceed
                registerValidSW(swUrl, config);
            }
        })
        .catch((err) => {
            // Likely offline; defer to the existing SW if any
            console.warn('SW validation fetch failed (offline or blocked):', err);
        });
}

/**
 * Wire update/install handlers to surface app update lifecycle.
 */
function wireUpdateHandlers(registration, config) {
    // Fired when a new SW is found
    registration.onupdatefound = () => {
        const installing = registration.installing;
        if (!installing) return;

        installing.onstatechange = () => {
            if (installing.state === 'installed') {
                if (navigator.serviceWorker.controller) {
                    // New content available
                    try {
                        config?.onUpdate?.(registration);
                    } catch (e) {
                        console.error('onUpdate handler threw:', e);
                    }
                } else {
                    // First install: content cached for offline use
                    try {
                        config?.onSuccess?.(registration);
                    } catch (e) {
                        console.error('onSuccess handler threw:', e);
                    }
                }
            }
        };
    };
}

/**
 * Visibility-aware polling for updates (every 30m while tab visible).
 * Also triggers an update check on visibility change to 'visible'.
 */
function startVisibilityAwarePolling(registration) {
    const UPDATE_MS = 30 * 60 * 1000; // 30 minutes

    const maybeUpdate = () => {
        // Avoid spamming updates if the page is hidden
        if (document.visibilityState === 'visible') {
            registration.update().catch((e) => {
                // benign: update() may fail offline
                console.debug('SW update() failed (likely offline):', e?.message || e);
            });
        }
    };

    // Initial check a bit after load to let the app settle
    setTimeout(maybeUpdate, 10000); // 10 seconds

    // Poll while visible
    const intervalId = setInterval(maybeUpdate, UPDATE_MS);

    // Kick an update check when tab becomes visible
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') maybeUpdate();
    });

    // Clean up on unload (best-effort)
    window.addEventListener('beforeunload', () => clearInterval(intervalId));
}

/**
 * Reload the page once when a waiting/installed SW takes control.
 * Prevents infinite reload loops.
 */
function wireSingleReloadOnControllerChange() {
    let hasReloaded = false;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (hasReloaded) return;
        hasReloaded = true;
        // Allow current microtask queue to flush; then reload
        setTimeout(() => window.location.reload(), 0);
    });
}

/**
 * Unregister the service worker and attempt to clear caches.
 */
export function unregister() {
    if (!supportsSW) return;

    navigator.serviceWorker.ready
        .then(async (registration) => {
            await registration.unregister();
            // Best-effort cache cleanup (names are app-specific)
            if (window.caches?.keys) {
                try {
                    const keys = await caches.keys();
                    await Promise.all(keys.map((k) => caches.delete(k)));
                } catch (e) {
                    console.warn('Cache cleanup failed:', e);
                }
            }
        })
        .catch((error) => {
            console.error('SW unregistration failed:', error);
        });
}

/**
 * Optional helper your UI can call after showing "New version available" prompt.
 * Forces the waiting SW to activate immediately.
 * In your SW, handle 'SKIP_WAITING' message: self.addEventListener('message', ...).
 */
export async function skipWaitingAndReload() {
    try {
        const reg = await navigator.serviceWorker.getRegistration();
        const waiting = reg && reg.waiting;
        if (!waiting) return false;

        // Tell SW to skip waiting; your SW must call self.skipWaiting() on this message.
        waiting.postMessage({ type: 'SKIP_WAITING' });

        // When the new SW takes control, wireSingleReloadOnControllerChange() will reload once.
        return true;
    } catch (e) {
        console.error('skipWaitingAndReload failed:', e);
        return false;
    }
}
